import React, { forwardRef, ForwardedRef, ReactElement, ComponentProps, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Skeleton } from '../feedback/Skeleton';
import { Tooltip } from '../feedback/Tooltip';
import { IconClock } from '../icon/IconClock';
import { IconDeclinedFilled } from '../icon/IconDeclinedFilled';
import { IconKebab } from '../icon/IconKebab';
import { IconLimited } from '../icon/IconLimited';
import { IconProduct } from '../icon/IconProduct';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { MediaCover } from '../media/MediaCover';
import { Typography } from '../typography/Typography';
import { MediaPreview } from './MediaPreview';
import { PostStatus } from './constants';
import { getContentIcon } from './getContentIcon';
import type { PostUI } from './types';

const DEFAULT_ICON_PROPS = {
    color: 'var(--control-secondary-blur-color)',
    size: 20,
};

const IconWrapper = styled(Box).attrs((props) => ({
    position: 'absolute',
    p: 0.25,
    borderRadius: 1,
    background: 'var(--control-secondary-blur-background)',
    backdropFilter: 'blur(20px)',
    ...props,
}))``;

const renderStatusElement = (
    status: PostUI['status'],
    props: ComponentProps<typeof Box>,
): ReactElement | null => {
    if (status === PostStatus.MODERATION) {
        return (
            <Tooltip
                trigger={
                    <IconWrapper cursor="help" {...props}>
                        <IconClock size={20} color="var(--control-secondary-color)" />
                    </IconWrapper>
                }
            >
                <Typography variant="caption" color="var(--color-invert)">
                    Пост на модерации
                </Typography>
            </Tooltip>
        );
    }

    if (status === PostStatus.LIMITED) {
        return (
            <Tooltip
                trigger={
                    <IconWrapper background="var(--color-warning)" cursor="help" {...props}>
                        <IconLimited size={20} color="#FFFFFF" />
                    </IconWrapper>
                }
            >
                <Typography variant="caption" color="var(--color-invert)">
                    Пост виден только в профиле
                </Typography>
            </Tooltip>
        );
    }

    if (status === PostStatus.DECLINED) {
        return (
            <Tooltip
                trigger={
                    <IconWrapper background="var(--color-danger)" cursor="help" {...props}>
                        <IconDeclinedFilled size={20} color="var(--control-secondary-blur-color)" />
                    </IconWrapper>
                }
            >
                <Typography variant="caption" color="var(--color-invert)">
                    Пост отклонен
                </Typography>
            </Tooltip>
        );
    }

    return null;
};

interface PostCompactCardProps {
    post: PostUI;
    renderMenu?: (trigger: ReactElement) => ReactElement | null;
    onPostCardClick?: VoidFunction;
    fromBusinessId?: string;
    fromCollectionId?: string;
}

export const PostCardCompact = forwardRef(
    (props: PostCompactCardProps, ref: ForwardedRef<HTMLDivElement>) => {
        const { post, renderMenu } = props;
        const ContentIcon = getContentIcon(post.content);
        const navigate = useNavigate();

        const handleClick = useCallback(() => {
            const { onPostCardClick, fromCollectionId, fromBusinessId } = props;

            // Прокидываем id сущности, из которой мы пришли в карточку поста
            // это нужно для аналитики
            navigate(post.url, {
                state: {
                    fromCollectionId,
                    fromBusinessId,
                },
            });

            onPostCardClick?.();
        }, [navigate, props]);

        return (
            <Box ref={ref} position="relative" onClick={handleClick} cursor="pointer">
                <MediaCover width="100%" height="100%" ratio={1}>
                    <MediaPreview media={post.content[0]} namespace="grid" />
                </MediaCover>
                {ContentIcon && (
                    <IconWrapper right={1} bottom={1}>
                        <ContentIcon {...DEFAULT_ICON_PROPS} />
                    </IconWrapper>
                )}
                {renderStatusElement(post.status, { top: 1, left: 1 })}
                {renderMenu &&
                    renderMenu(
                        <Box position="absolute" right={1} top={1}>
                            <IconButton variant="filled" shape="rect" size="xs">
                                <IconKebab fontSize={16} />
                            </IconButton>
                        </Box>,
                    )}
                {post.products.items.length > 0 && (
                    <Box position="absolute" right={renderMenu ? 5 : 1} top={1}>
                        <IconButton variant="filled" shape="rect" size="xs">
                            <IconProduct fontSize={16} />
                        </IconButton>
                    </Box>
                )}
            </Box>
        );
    },
);

export const PostCardCompactSkeleton = styled(Skeleton).attrs({
    variant: 'rect',
    width: '100%',
    height: '100%',
})``;
